import { Card } from "@/components/ui/card";

/**
 * FormSection — unified, framed section used across all "fiches" in Le Desk
 * (user profile, task creation, future documents / pointage forms…).
 *
 * Look & feel:
 *  - Dark card background matching the app theme
 *  - Left Vertec-blue icon + uppercase title with subtle tracking
 *  - Bottom border separator between the header and the body
 *
 * Props:
 *   title           string  Section title (required).
 *   icon            node    Lucide icon element rendered on the left.
 *   right           node    Optional right-aligned slot in the header
 *                           (badge, meta info, ...).
 *   testIdPrefix    string  Prefix for the automatic data-testid attribute —
 *                           defaults to "section". Kept configurable so we
 *                           preserve existing test suites that already look
 *                           for "section-…" or "uf-section-…".
 *   className       string  Extra classes appended to the Card.
 */
export default function FormSection({
  title,
  icon,
  right = null,
  iconColor = null,
  testIdPrefix = "section",
  className = "",
  children,
}) {
  // Design system rule (Le Desk):
  //  - Section frame is always the same crisp #3A3A3A border (a hair lighter
  //    than the app's default #333) so the box detaches from the wallpaper.
  //  - Subtle drop shadow gives a tactile card feel without stealing focus.
  //  - Applied globally via this component — every future form section in
  //    Le Desk (Documents, Pointage…) picks it up automatically.
  return (
    <Card
      className={`form-section bg-card p-5 border-[#3A3A3A] shadow-[0_2px_8px_rgba(0,0,0,0.3)] ${className}`}
      data-testid={`${testIdPrefix}-${String(title || "").toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#3A3A3A]">
        <div style={iconColor ? { color: iconColor } : undefined} className={iconColor ? "" : "text-vertec-blue"}>
          {icon}
        </div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground flex-1">
          {title}
        </h3>
        {right}
      </div>
      {children}
    </Card>
  );
}
